'use strict';
const moment = require('moment');
const { gql } = require('graphql-tag');
const locations = require('../locations');
const utilities = require('../utilities');
const errors = require('../errors');
const { moveObjSchema, requestSchema, moveSchema, vehicleSchema, locationSchema, configSchema } = require('./schema');
const { getPropValue } = require('../utilities');

const log = true;

const defaultWorkflowset = {
  id: 1,
  pickup_workflow_id: 1,
  delivery_workflow_id: 2,
};

const defaultSla = {
  id: 1,
  duration_hrs: 48,
};

const conciergeSla = {
  id: 1,
  duration_hrs: 48,
};

var _parent = {};

var moves = {
  init: p => (_parent = p),

  /**
   * Types psuedo enum
   *
   * Value and code are the same here as there's no numeric value of a type
   */

  types: Object.freeze({
    DRIVE: {
      value: 'drive',
      name: 'Drive',
      code: 'drive',
      desc: 'A move representing a vehicle being moved for a customer.',
    },
    RIDE: {
      value: 'ride',
      name: 'Ride',
      code: 'ride',
      desc: 'A move representing a return ride taken by our driver returning to their car.',
    },
  }),

  /**
   * Check against the list of types to ensure a type is valid
   *
   * @param  {String} type - A type code to check against the valid list of type codes
   * @returns {bool} True if the type is valid
   */
  isValidType: type => {
    return moves.types.map(t => t.code).includes(type);
  },

  /**
   * Status codes psuedo enum
   *
   * Value prop intended to serve as a poor mans sequence indication here.
   *
   */

  statusCodes: Object.freeze({
    UNPLANNED: { value: 10, name: 'Unplanned', code: null, desc: '' },
    CANCELED: { value: 20, name: 'Canceled', code: 'canceled', desc: '' },
    FAILED: { value: 30, name: 'Failed', code: 'failed', desc: '' },
    DISPATCHED: { value: 40, name: 'Dispatched', code: 'dispatched', desc: '' },
    PICKUP_STARTED: { value: 50, name: 'Pickup Started', code: 'pickup started', desc: '' },
    PICKUP_ARRIVED: { value: 60, name: 'Pickup Arrived', code: 'pickup arrived', desc: '' },
    PICKUP_SUCCESSFUL: { value: 70, name: 'Pickup Successful', code: 'pickup successful', desc: '' },
    DELIVERY_STARTED: { value: 80, name: 'Delivery Started', code: 'delivery started', desc: '' },
    DELIVERY_ARRIVED: { value: 90, name: 'Delivery Arrived', code: 'delivery arrived', desc: '' },
    DELIVERY_SUCCESSFUL: { value: 100, name: 'Delivery Successful', code: 'delivery successful', desc: '' },
  }),

  /**
   * Fetches a matching move object, returning it in an array inside of a response object.
   *
   * @param  {BigInteger} id - The ID of a move.
   * @param  {BigInteger} customer_id - The ID of the customer.
   * @return  {Object} The response object containing the matching move.
   */

  getById: async (id, customer_id) => {
    const res = await _parent.gql.query(
      gql`
        query getMoveById($id: bigint!, $customer_id: bigint!) {
          moves(where: { id: { _eq: $id }, customer_id: { _eq: $customer_id }, move_type: { _ilike: "drive" } }) {
            ...Move
          }
        }
        ${_parent.fragments.getterMove()}
      `,
      { id, customer_id }
    );
    return res;
  },

  /**
   * Fetches a matching ride move object, returning it in an array inside of a response object.
   *
   * @param  {BigInteger} id - The ID of a move.
   * @return  {Object} The response object containing the matching move.
   */

  getRideMoveById: async id => {
    const res = await _parent.gql.query(
      gql`
        query findRideMove($id: bigint!) {
          moves(where: { id: { _eq: $id } }) {
            id
            customer_id
            driver_id
            driver_name
            ride_type
            customer {
              id
              config
            }
            lane {
              id
              pickup {
                id
                address
                latitude
                longitude
                name
              }
              delivery {
                id
                address
                latitude
                longitude
                name
              }
            }
            ride {
              id
              active_attempt
              active_estimate_attempts
              latitude
              longitude
              passenger_phone
              ride_move_id
              status
              activeAttempt {
                id
                ride_id
                status
              }
              activeEstimateAttempts {
                id
                ride_id
                ride_type
                estimated_ride_cost
                estimate_id
                status
              }
              attempts(order_by: { createdat: desc_nulls_last }) {
                id
                ride_id
                ride_type
                estimated_ride_cost
                estimate_id
                status
              }
            }
          }
        }
      `,
      { id }
    );
    return res;
  },

  /**
   * Fetches an array of duplicated move objects, returning it in an array inside of a response object.
   *
   * @param  {BigInteger} customer_id - The ID of the customer.
   * @param  {String} field_name - The name of the field to check dupes on.
   * @param  {String} field_value - The value to check for dupes.
   * @return  {Object} The response object containing the duplicated move(s).
   */

  getDupes: async (customer_id, field_name, field_value) => {
    const res = await _parent.gql.query(
      gql`
        query get_move_dupes($customer_id: bigint!, $field_value: String) {
          moves(
            where: {
              active: { _eq: 1 }
              customer_id: { _eq: $customer_id }
              _or: [{ status: { _is_null: true } }, { status: { _neq: "delivery successful" } }]
              cancel_status: { _is_null: true }
              ${field_name}: { _eq: $field_value }
            }
          ) {
            id
          }
        }
      `,
      { customer_id, field_value }
    );
    return res;
  },

  /**
   * Fetches an array of duplicated move objects, returning it in an array inside of a response object.
   *
   * @param  {BigInteger} customer_id - The ID of the customer.
   * @param  {String} field_name - The name of the field to check dupes on.
   * @param  {String} field_value_one - The first value to check for dupes.
   * @param  {String} field_value_two - The second value to check for dupes.
   * @return  {Object} The response object containing the duplicated move(s).
   */

  getPairedDupes: async (customer_id, field_name, field_value_one, field_value_two) => {
    const res = await _parent.gql.query(
      gql`
        query get_move_paired_dupes($customer_id: bigint!, $field_value_one: String, $field_value_two: String) {
          moves(
            where: {
              active: { _eq: 1 }
              customer_id: { _eq: $customer_id }
              cancel_status: { _is_null: true }
              _and: [{_or: [{status: {_is_null: true}}, {status: {_neq: "delivery successful"}}]}, {_or: [{ ${field_name}: { _eq: $field_value_one } }, { ${field_name}: { _eq: $field_value_two } }]}]
            }
          ) {
            id
          }
        }
      `,
      { customer_id, field_value_one, field_value_two }
    );
    return res;
  },

  /**
   * Fetches a workflowset by id
   *
   * @param  {number} workflowset_id - The ID of the workflowset.
   * @param  {timestamptz} move_time - The time of the move. Used to get the current raterulegroup.
   * @return  {object} The response object containing the workflowset.
   */

  getWorkflowset: async (workflowset_id, customer_id, move_time = 'now()') => {
    const res = await _parent.gql.query(
      gql`
        query get_workflowset($workflowset_id: Int!, $customer_id: bigint!, $move_time: timestamptz!) {
          workflowsets(where: { id: { _eq: $workflowset_id }, customer_id: { _eq: $customer_id } }) {
            id
            pickup_workflow_id
            delivery_workflow_id
            raterulegroups(
              where: { begin_date: { _lte: $move_time }, end_date: { _gt: $move_time } }
              order_by: { begin_date: desc }
              limit: 1
            ) {
              id
              sla {
                id
                name
                label
                description
                duration_hrs
              }
            }
          }
        }
      `,
      { workflowset_id, customer_id, move_time }
    );
    return res;
  },

  /**
   * Fetches a workflowset by type
   *
   * @param  {string} workflowset_type - The type of the workflowset.
   * @param  {timestamptz} move_time - The time of the move. Used to get the current raterulegroup.
   * @return  {object} The response object containing the workflowset.
   */

  getWorkflowsetByType: async (workflowset_type, customer_id, move_time = 'now()') => {
    const res = await _parent.gql.query(
      gql`
        query get_workflowset_by_type($workflowset_type: String!, $customer_id: bigint!, $move_time: timestamptz!) {
          workflowsets(where: { type: { _eq: $workflowset_type }, customer_id: { _eq: $customer_id } }) {
            id
            pickup_workflow_id
            delivery_workflow_id
            raterulegroups(
              where: { begin_date: { _lte: $move_time }, end_date: { _gt: $move_time } }
              order_by: { begin_date: desc }
              limit: 1
            ) {
              id
              sla {
                id
                name
                label
                description
                duration_hrs
              }
            }
          }
        }
      `,
      { workflowset_type, customer_id, move_time }
    );
    return res;
  },

  /**
   * Fetches a couple workflowsets by id
   *
   * @param  {number} workflowset_id_1 - The ID of the first workflowset.
   * @param  {number} workflowset_id_2 - The ID of the second workflowset.
   * @param  {timestamptz} move_time - The time of the move. Used to get the current raterulegroup.
   * @return  {object} The response object containing the workflowsets.
   */

  getPairedWorkflowsets: async (workflowset_id_1, workflowset_id_2, move_time = 'now()') => {
    const res = await _parent.gql.queryWithApollo(
      gql`
        query get_paired_workflowsets($workflowset_id_1: Int!, $workflowset_id_2: Int!, $move_time: timestamptz!) {
          workflowsets1: workflowsets(where: { id: { _eq: $workflowset_id_1 } }) {
            id
            pickup_workflow_id
            delivery_workflow_id
            raterulegroups(
              where: { begin_date: { _lte: $move_time }, end_date: { _gt: $move_time } }
              order_by: { begin_date: desc }
              limit: 1
            ) {
              id
              sla {
                id
                name
                label
                description
                duration_hrs
              }
            }
          }
          workflowsets2: workflowsets(where: { id: { _eq: $workflowset_id_2 } }) {
            id
            pickup_workflow_id
            delivery_workflow_id
            raterulegroups(
              where: { begin_date: { _lte: $move_time }, end_date: { _gt: $move_time } }
              order_by: { begin_date: desc }
              limit: 1
            ) {
              id
              sla {
                id
                name
                label
                description
                duration_hrs
              }
            }
          }
        }
      `,
      { workflowset_id_1, workflowset_id_2, move_time },
      _parent.config.apollo_client
    );
    return res;
  },

  /**
   * Fetches an SLA by id
   *
   * @param {number} sla_id - The ID of the SLA.
   * @returns {object} The response object containing the SLA.
   */

  getSla: async sla_id => {
    const res = await _parent.gql.query(
      gql`
        query get_sla($sla_id: bigint!) {
          slas(where: { id: { _eq: $sla_id } }) {
            id
            name
            label
            description
            duration_hrs
          }
        }
      `,
      { sla_id }
    );
    return res;
  },

  /**
   * Fetches a couple slas by id
   *
   * @param  {number} sla_id_1 - The ID of the first SLA.
   * @param  {number} sla_id_2 - The ID of the second SLA.
   * @return  {object} The response object containing the SLAs.
   */

  getPairedSlas: async (sla_id_1, sla_id_2) => {
    const res = await _parent.gql.queryWithApollo(
      gql`
        query get_paired_slas($sla_id_1: bigint!, $sla_id_2: bigint!) {
          slas1: slas(where: { id: { _eq: $sla_id_1 } }) {
            id
            name
            label
            description
            duration_hrs
          }
          slas2: slas(where: { id: { _eq: $sla_id_2 } }) {
            id
            name
            label
            description
            duration_hrs
          }
        }
      `,
      { sla_id_1, sla_id_2 },
      _parent.config.apollo_client
    );
    return res;
  },

  /**
   * Fetches the default SLA
   *
   * @returns {object} The response object containing the SLA.
   */

  getDefaultSla: async () => {
    const res = await _parent.gql.query(
      gql`
        query get_default_sla {
          slas(where: { name: { _eq: "default" } }) {
            id
            name
            label
            description
            duration_hrs
          }
        }
      `
    );
    return res;
  },

  /**
   * Fetches the concierge SLA
   *
   * @returns {object} The response object containing the SLA.
   */

  getConciergeSla: async () => {
    const res = await _parent.gql.query(
      gql`
        query get_concierge_sla {
          slas(where: { name: { _eq: "default_concierge" } }) {
            id
            name
            label
            description
            duration_hrs
          }
        }
      `
    );
    return res;
  },

  /**
   * Create one or more new moves in the database
   *
   * @param {array} moves - An array of move record objects
   * @param {object} options - An optional options object
   * @param {object} [options.response_fragment] - Optionally set the GQL mutation's returning response
   * @returns {object} A graphql mutation response
   */

  create: async (objs, options = { response_fragment: _parent.fragments.move() }) => {
    log && console.log('moves.create.options', options);
    const res = await _parent.gql.mutation(
      gql`
        mutation sdk_moves_create($objectArray: [moves_insert_input!]!) {
          insert_moves(objects: $objectArray) {
            affected_rows
            returning {
              ...Move
            }
          }
        }
        ${options.response_fragment}
      `,
      {
        objectArray: objs,
      }
    );
    return res;
  },

  /**
   * Update an move record in the database
   *
   * @param {number} id - An id for the move record to update
   * @param {array} move - A partial move object with updated values
   * @returns {object} A graphql mutation response
   */

  update: async (id, obj) => {
    const res = await _parent.gql.mutation(
      gql`
        mutation sdk_moves_update($id: bigint!, $move: moves_set_input!) {
          update_moves(where: { id: { _eq: $id } }, _set: $move) {
            affected_rows
          }
        }
      `,
      {
        id: id,
        move: obj,
      }
    );
    return _parent.gql.injectAffectedRowsAtRoot(res);
  },

  /**
   * Updates a move object with matching ID, then returns a response object with the GQL response.
   *
   * @param  {BigInteger} id - The ID of a move.
   * @param   {Object} variables - Key value pairs of field(key) and value to set
   * @return  {Object} The response object confirming the update.
   */
  updateById: async (id, variables) => {
    const res = await _parent.gql.mutation(
      gql`mutation updateMoveById($id: bigint!) {
          update_moves(
            where: {id: {_eq: $id}},
            _set: ${_parent.gql.buildUpdateClauseString(variables)}
            ) {
              affected_rows
              returning {
                ...Move
              }
            }
          }
          ${_parent.fragments.updaterMove()}
        `,
      { id: id }
    );
    return _parent.gql.injectAffectedRowsAtRoot(res);
  },

  /**
   * Get Move with nested lane, charge and workflow for building API response.
   * Only returns active drive moves.
   *
   * @param  {BigInteger} id - The ID of a move.
   * @return  {Object} The response object confirming the update.
   */
  getNestedMoveById: async id => {
    const res = await _parent.gql.query(
      gql`
        query api_moves_getMoveById($id: bigint) {
          moves(where: { id: { _eq: $id }, active: { _eq: "1" }, move_type: { _ilike: "drive" } }) {
            id
            cancel_reason
            cancel_status
            customer_id
            dealer_contact
            deliver_by
            delivery_arrived
            delivery_started
            delivery_successful
            delivery_workflow_data
            earliest_available_time
            move_details
            pickup_arrived
            pickup_started
            pickup_successful
            pickup_workflow_data
            ready_by
            reference_num
            settled
            status
            vehicle_color
            vehicle_make
            vehicle_model
            vehicle_stock
            vehicle_vin
            vehicle_year
            lane {
              id
              pickup {
                id
                address
                latitude
                longitude
                name
                place_id
              }
              delivery {
                id
                address
                latitude
                longitude
                name
                place_id
              }
            }
            sla {
              id
              name
              label
              description
              duration_hrs
            }
            workflowset {
              id
              name
              type
              description
              pickupWorkflow {
                id
                steps
              }
              deliveryWorkflow {
                id
                steps
              }
              fuelWorkflow {
                id
                steps
              }
            }
            pickup_photos: vehiclephotos(where: { workflow: { type: { _eq: "pickup" } } }) {
              id
              status
              step_id
              name
              url
              location
              time: createdat
            }
            delivery_photos: vehiclephotos(where: { workflow: { type: { _eq: "delivery" } } }) {
              id
              status
              step_id
              name
              url
              location
              time: createdat
            }
            charge: accountsReceivable {
              invoice_id
              type
              status
              disputed
              dispute_reason
              discount_amount
              discount_reason
              due_amount
              paid_amount
              details(where: { amount: { _gt: "0" } }) {
                name
                notes
                amount
                documents {
                  name
                  url
                  mime_type
                  created_at
                }
                mileage_band: raterule {
                  type
                  rate
                  class
                  distance_start
                  distance_end
                  contract: raterulegroup {
                    begin_date
                    end_date
                    workflowset_id
                  }
                }
              }
            }
          }
        }
      `,
      { id }
    );
    return res;
  },

  //////////////////////////////////////// MOVE REQUEST CONVERSION FUNCTIONS ////////////////////////////////////////

  /**
   * Convert a move request object from API to match our DB schema
   *
   * @param {object} req - The request object to convert
   * @param {boolean} isConcierge - Boolean to determine which schema to convert to
   * @param {number} laneDuration - The duration of the lane in seconds (defaults to 90 minutes)
   * @returns {object} Returns an object that matches our schema
   */

  convertMoveToDBSchema: (req, workflowset, sla, isConcierge = false, laneDuration = 5400) => {
    try {
      let newMoveReq = {};

      // Prevent crash if request does not contain all the nested objects
      let move = req.move || {};
      if (isConcierge) move = req.customer_move || {};
      let vehicle = move.vehicle || {};

      // Calculate deliver by time from SLA
      let deliverBy = moment(req.ready_by_time_utc)
        .add(sla.duration_hrs || 48, `hour`)
        .format();

      // Convert request object to match our DB schema
      newMoveReq = {
        class: `stranded`,
        config: move.config || null,
        createdBy: `API-${req.customer_id || req.rooftop || `unknown`}`,
        customer_id: req.customer_id || req.rooftop || null,
        dealer_contact: move.dealer_contact || null,
        deliver_by: deliverBy || null,
        delivery_workflow_data: move.delivery_workflow_data || null,
        delivery_workflow_id: workflowset.delivery_workflow_id || null,
        lane_id: move.lane_id || null,
        manual_flag: vehicle.manual ? true : false,
        move_details: move.special_instructions || null,
        payer_id: req.third_party_payer || null,
        pickup_time: req.ready_by_time_utc || null,
        pickup_workflow_data: move.pickup_workflow_data || null,
        pickup_workflow_id: workflowset.pickup_workflow_id || null,
        ready_by: req.ready_by_time_utc || null,
        reference_num: move.reference_num || null,
        sequence: 1,
        sla_id: sla.id || null,
        vehicle_color: vehicle.color || null,
        vehicle_make: vehicle.make || null,
        vehicle_model: vehicle.model || null,
        vehicle_stock: vehicle.stock || null,
        vehicle_vin: vehicle.vin || null,
        vehicle_year: vehicle.year || null,
        vehicle_gross_weight_lbs: vehicle.gross_weight_lbs || null,
        workflowset_id: workflowset.id || null,
      };

      // If concierge move, set related values
      if (isConcierge) {
        //For concierge return moves, if the lane duration is less than 90 minutes, use 90 minutes
        const laneDurationWithMinimum = laneDuration < 5400 ? 5400 : laneDuration;
        const laneDurationForConcierge = req.customer_action === `return` ? laneDurationWithMinimum : laneDuration;
        deliverBy = moment(req.appointment_time_utc)
          .add(sla.duration_hrs || 1, `hour`)
          .add(laneDurationForConcierge, `seconds`)
          .format();
        newMoveReq = {
          ...newMoveReq,
          consumer_at_pickup: req.customer_action ? (req.customer_action === `pickup` ? 1 : 0) : null,
          consumer_name: req.customer_name || null,
          consumer_phone: req.customer_phone || null,
          deliver_by: deliverBy || null,
          pickup_time: req.appointment_time_utc || null,
          ready_by: req.appointment_time_utc || null,
        };
      }

      // Return converted move object
      return newMoveReq;
    } catch (err) {
      console.error(`convertMoveToDBSchema() - Conversion to DB schema failed:`, err);
      return null;
    }
  },

  /**
   * Convert a move request object from API to match our DB schema
   *
   * @param {object} req - The request object to convert
   * @param {boolean} isConcierge - Boolean to determine which schema to convert to
   * @param {boolean} customerFirst - Boolean to determine if concierge move is first
   * @param {number} laneDuration - The duration of the lane in seconds (defaults to 90 minutes)
   * @returns {object} Returns an object that matches our schema
   */

  convertPairedMovesToDBSchema: (
    req,
    workflowsets,
    slas,
    isConcierge = false,
    customerFirst = false,
    laneDuration = 5400
  ) => {
    try {
      let newMoveReq1 = {};
      let newMoveReq2 = {};

      // Prevent crash if request does not contain all the nested objects
      let move1 = req.move_one || {};
      let move2 = req.move_two || {};

      // If concierge+loaner move, reset moves accordingly
      if (isConcierge) {
        move1 = req.loaner_move || {};
        move2 = req.customer_move || {};

        // If the concierge move is first, reset moves accordingly
        if (customerFirst) {
          move1 = req.customer_move || {};
          move2 = req.loaner_move || {};
        }
      }

      let vehicle1 = move1.vehicle || {};
      let vehicle2 = move2.vehicle || {};

      // Calculate deliver by time from SLA
      let deliverBy = moment(req.ready_by_time_utc)
        .add(slas[0].duration_hrs || 48, `hour`)
        .format();

      // Convert request object to match our DB schema
      newMoveReq1 = {
        class: isConcierge ? `stranded` : `base`,
        config: move1.config || null,
        createdBy: `API-${req.customer_id || req.rooftop || `unknown`}`,
        customer_id: req.customer_id || req.rooftop || null,
        dealer_contact: move1.dealer_contact || null,
        deliver_by: deliverBy || null,
        delivery_workflow_data: move1.delivery_workflow_data || null,
        delivery_workflow_id: workflowsets[0].delivery_workflow_id || null,
        lane_id: move1.lane_id || null,
        manual_flag: vehicle1.manual ? true : false,
        move_details: move1.special_instructions || null,
        payer_id: req.third_party_payer || null,
        pickup_time: req.ready_by_time_utc || null,
        pickup_workflow_data: move1.pickup_workflow_data || null,
        pickup_workflow_id: workflowsets[0].pickup_workflow_id || null,
        ready_by: req.ready_by_time_utc || null,
        reference_num: move1.reference_num || null,
        sequence: 1,
        sla_id: slas[0].id || null,
        vehicle_color: vehicle1.color || null,
        vehicle_make: vehicle1.make || null,
        vehicle_model: vehicle1.model || null,
        vehicle_stock: vehicle1.stock || null,
        vehicle_vin: vehicle1.vin || null,
        vehicle_year: vehicle1.year || null,
        workflowset_id: workflowsets[0].id || null,
      };
      newMoveReq2 = {
        class: isConcierge ? `stranded` : `base`,
        config: move2.config || null,
        createdBy: `API-${req.customer_id || req.rooftop || `unknown`}`,
        customer_id: req.customer_id || req.rooftop || null,
        dealer_contact: move2.dealer_contact || null,
        deliver_by: deliverBy || null,
        delivery_workflow_data: move2.delivery_workflow_data || null,
        delivery_workflow_id: workflowsets[1].delivery_workflow_id || null,
        lane_id: move2.lane_id || null,
        manual_flag: vehicle2.manual ? true : false,
        move_details: move2.special_instructions || null,
        payer_id: req.third_party_payer || null,
        pickup_time: req.ready_by_time_utc || null,
        pickup_workflow_data: move2.pickup_workflow_data || null,
        pickup_workflow_id: workflowsets[1].pickup_workflow_id || null,
        ready_by: req.ready_by_time_utc || null,
        reference_num: move2.reference_num || null,
        sequence: 2,
        sla_id: slas[1].id || null,
        vehicle_color: vehicle2.color || null,
        vehicle_make: vehicle2.make || null,
        vehicle_model: vehicle2.model || null,
        vehicle_stock: vehicle2.stock || null,
        vehicle_vin: vehicle2.vin || null,
        vehicle_year: vehicle2.year || null,
        workflowset_id: workflowsets[1].id || null,
      };

      // If concierge move, set related values
      if (isConcierge) {
        //For concierge return moves, if the lane duration is less than 90 minutes, use 90 minutes
        const laneDurationWithMinimum = laneDuration < 5400 ? 5400 : laneDuration;
        const laneDurationForConcierge = req.customer_action === `return` ? laneDurationWithMinimum : laneDuration;
        deliverBy = moment(req.appointment_time_utc)
          .add(slas[0].duration_hrs || 1, `hour`)
          .add(laneDurationForConcierge, `seconds`)
          .format();
        newMoveReq1 = {
          ...newMoveReq1,
          consumer_name: req.customer_name || null,
          consumer_phone: req.customer_phone || null,
          deliver_by: deliverBy || null,
          pickup_time: req.appointment_time_utc || null,
          ready_by: req.appointment_time_utc || null,
        };
        newMoveReq2 = {
          ...newMoveReq2,
          consumer_name: req.customer_name || null,
          consumer_phone: req.customer_phone || null,
          deliver_by: deliverBy || null,
          pickup_time: req.appointment_time_utc || null,
          ready_by: req.appointment_time_utc || null,
        };
      }

      // Return converted move array
      return [newMoveReq1, newMoveReq2];
    } catch (err) {
      console.error(`convertPairedMovesToDBSchema() - Conversion to DB schema failed:`, err);
      return null;
    }
  },

  /**
   * Convert a move response object back to API request structure
   *
   * @param {object} res - The response object of a move to convert
   * @param {boolean} isConcierge - Boolean to determine which schema to convert to
   * @returns {object} Returns an object that matches the API structure
   */

  convertMoveToAPISchema: async (res, isConcierge = false) => {
    try {
      // Get pickup and delivery
      let pickup = getPropValue(res, `lane.pickup`);
      let delivery = getPropValue(res, `lane.delivery`);

      // Get workflowset object
      let workflowset = getPropValue(res, `workflowset`);

      // Set move names for response
      let moveName = isConcierge ? `customer_move` : `move`;

      // Convert response object to match the API structure
      let newRes = {
        [moveName]: {
          id: res.id || null,
          rooftop: res.customer_id,
          third_party_payer: res.payer_id,
          reference_num: res.reference_num || null,
          dealer_contact: res.dealer_contact || null,
          special_instructions: res.move_details || null,
          vehicle: {
            vin: res.vehicle_vin || null,
            stock: res.vehicle_stock || null,
            year: res.vehicle_year || null,
            make: res.vehicle_make || null,
            model: res.vehicle_model || null,
            color: res.vehicle_color || null,
            manual: res.manual_flag ? true : false,
            gross_weight_lbs: res.vehicle_gross_weight_lbs || null,
          },
          pickup_location: locations.buildLocationResObj(pickup),
          delivery_location: locations.buildLocationResObj(delivery),
          pickup_workflow_data: res.pickup_workflow_data || null,
          delivery_workflow_data: res.delivery_workflow_data || null,
          config: res.config || null,
          move_uri: `https://api.hopdrive.com/v1/moves/${res.id}`,
          lane_uri: `https://api.hopdrive.com/v1/lanes/${res.lane_id}`,
        },
      };

      // Give back the workflowset if it has one
      if (workflowset)
        newRes[moveName].workflowset = {
          id: workflowset.id,
          name: workflowset.name,
          label: workflowset.label || workflowset.name,
          description: workflowset.description,
        };

      // If not a concierge move, set related values
      if (!isConcierge) {
        //The response object expects a different timestamp syntax than the one GQL returns
        //https://stackoverflow.com/questions/5631384/remove-everything-after-a-certain-character
        let formattedReadyBy = res.ready_by ? res.ready_by.split('+')[0] + 'Z' : null;
        let formattedDeliverBy = res.deliver_by ? res.deliver_by.split('+')[0] + 'Z' : null;
        newRes = {
          ...newRes,
          ready_by_time_utc: formattedReadyBy || null,
          deliver_by_time_utc: formattedDeliverBy || null,
        };
      }
      // Else set concierge related values
      else {
        //The response object expects a different timestamp syntax than the one GQl returns
        //https://stackoverflow.com/questions/5631384/remove-everything-after-a-certain-character
        let formattedReadyBy = res.ready_by ? res.ready_by.split('+')[0] + 'Z' : null;
        let formattedDeliverBy = res.deliver_by ? res.deliver_by.split('+')[0] + 'Z' : null;
        newRes = {
          ...newRes,
          ready_by_time_utc: formattedReadyBy || null,
          deliver_by_time_utc: formattedDeliverBy || null,
          customer_name: res.consumer_name || null,
          customer_phone: res.consumer_phone || null,
          customer_action: res.consumer_at_pickup === 1 ? `pickup` : `return`,
        };
      }

      const earliestRes = await _parent.businesshours.handleEarliestAvailableTime(res.id);
      console.log('earliestRes single move', earliestRes);
      if (earliestRes) {
        newRes.message = earliestRes.earliestAvailableTimeMessage || null;
        newRes.earliest_available_time = earliestRes.earliestAvailableTime || null;
      }

      // Return converted move object
      return newRes;
    } catch (err) {
      console.error(`convertMoveToAPISchema() - Conversion to API structure failed:`, err);
      return null;
    }
  },

  /**
   * Convert a move response object back to API request structure
   *
   * @param {array} res - The response array of paired moves to convert
   * @param {boolean} isConcierge - Boolean to determine which schema to convert to
   * @param {boolean} customerFirst - Boolean to determine if concierge move is first
   * @returns {object} Returns an object that matches the API structure
   */

  convertPairedMovesToAPISchema: async (res, isConcierge = false, customerFirst = false) => {
    try {
      // Get pickup and delivery
      let pickup = getPropValue(res[0], `lane.pickup`);
      let delivery = getPropValue(res[1], `lane.delivery`);

      // Get workflowset objects
      let workflowset1 = getPropValue(res[0], `workflowset`);
      let workflowset2 = getPropValue(res[1], `workflowset`);

      // Set move names for response
      let move1Name = isConcierge ? (!customerFirst ? `loaner_move` : `customer_move`) : `move_one`;
      let move2Name = isConcierge ? (!customerFirst ? `customer_move` : `loaner_move`) : `move_two`;

      // Convert response object to match the API structure
      let newRes = {
        [move1Name]: {
          id: res[0].id || null,
          rooftop: res[0].customer_id,
          third_party_payer: res[0].payer_id,
          reference_num: res[0].reference_num || null,
          dealer_contact: res[0].dealer_contact || null,
          special_instructions: res[0].move_details || null,
          vehicle: {
            vin: res[0].vehicle_vin || null,
            stock: res[0].vehicle_stock || null,
            year: res[0].vehicle_year || null,
            make: res[0].vehicle_make || null,
            model: res[0].vehicle_model || null,
            color: res[0].vehicle_color || null,
            manual: res[0].manual_flag ? true : false,
            gross_weight_lbs: res[0].vehicle_gross_weight_lbs || null,
          },
          pickup_location: locations.buildLocationResObj(pickup),
          pickup_workflow_data: res[0].pickup_workflow_data || null,
          delivery_workflow_data: res[0].delivery_workflow_data || null,
          config: res[0].config || null,
          move_uri: `https://api.hopdrive.com/v1/moves/${res[0].id}`,
          lane_uri: `https://api.hopdrive.com/v1/lanes/${res[0].lane_id}`,
        },
        [move2Name]: {
          id: res[1].id || null,
          rooftop: res[1].customer_id,
          third_party_payer: res[1].payer_id,
          reference_num: res[1].reference_num || null,
          dealer_contact: res[1].dealer_contact || null,
          special_instructions: res[1].move_details || null,
          vehicle: {
            vin: res[1].vehicle_vin || null,
            stock: res[1].vehicle_stock || null,
            year: res[1].vehicle_year || null,
            make: res[1].vehicle_make || null,
            model: res[1].vehicle_model || null,
            color: res[1].vehicle_color || null,
            manual: res[1].manual_flag ? true : false,
            gross_weight_lbs: res[1].vehicle_gross_weight_lbs || null,
          },
          pickup_location: locations.buildLocationResObj(delivery),
          pickup_workflow_data: res[1].pickup_workflow_data || null,
          delivery_workflow_data: res[1].delivery_workflow_data || null,
          config: res[1].config || null,
          move_uri: `https://api.hopdrive.com/v1/moves/${res[1].id}`,
          lane_uri: `https://api.hopdrive.com/v1/lanes/${res[1].lane_id}`,
        },
      };

      // Give back the workflowset if it has one
      if (workflowset1)
        newRes[move1Name].workflowset = {
          id: workflowset1.id,
          name: workflowset1.name,
          label: workflowset1.label || workflowset1.name,
          description: workflowset1.description,
        };

      if (workflowset2)
        newRes[move2Name].workflowset = {
          id: workflowset2.id,
          name: workflowset2.name,
          label: workflowset2.label || workflowset2.name,
          description: workflowset2.description,
        };

      // If not a concierge move, set related values
      if (!isConcierge) {
        //The response object expects a different timestamp syntax than the one GQL returns
        //https://stackoverflow.com/questions/5631384/remove-everything-after-a-certain-character
        let formattedReadyBy = res.length > 0 && res[0].ready_by ? res[0].ready_by.split('+')[0] + 'Z' : null;
        let formattedDeliverBy = res.length > 0 && res[0].deliver_by ? res[0].deliver_by.split('+')[0] + 'Z' : null;

        newRes = {
          ...newRes,
          ready_by_time_utc: formattedReadyBy || null,
          deliver_by_time_utc: formattedDeliverBy || null,
        };
      }
      // Else set concierge related values
      else {
        //The response object expects a different timestamp syntax than the one GQL returns
        //https://stackoverflow.com/questions/5631384/remove-everything-after-a-certain-character
        let formattedReadyBy = res.length > 1 && res[0].ready_by ? res[0].ready_by.split('+')[0] + 'Z' : null;
        let formattedDeliverBy = res.length > 1 && res[0].deliver_by ? res[0].deliver_by.split('+')[0] + 'Z' : null;
        let formattedAppointment = res.length > 1 && res[1].ready_by ? res[1].ready_by.split('+')[0] + 'Z' : null;

        newRes = {
          ...newRes,
          ready_by_time_utc: formattedReadyBy || null,
          deliver_by_time_utc: formattedDeliverBy || null,
          appointment_time_utc: formattedAppointment || null,
          customer_name: res[0].consumer_name || null,
          customer_phone: res[0].consumer_phone || null,
          customer_action: !customerFirst ? `pickup` : `return`,
        };
      }

      const earliestRes = await _parent.businesshours.handleEarliestAvailableTimeRoundTrip([res[0].id, res[1].id]);
      console.log('earliestRes round trip', earliestRes);
      if (earliestRes) {
        newRes.message = earliestRes.earliestAvailableTimeMessage || null;
        newRes.move_one_earliest_available_time = earliestRes.earliestAvailableTime.moveOne || null;
        newRes.move_two_earliest_available_time = earliestRes.earliestAvailableTime.moveTwo || null;
      }

      // Return converted move object
      return newRes;
    } catch (err) {
      console.error(`convertPairedMovesToAPISchema() - Conversion to API structure failed:`, err);
      return null;
    }
  },

  //////////////////////////////////////// VALIDATION FUNCTIONS ////////////////////////////////////////

  /**
   * Validate and cleanse a single move
   *
   * @param {object} validationInput - A structured validation object to edit and return
   * @param {array} moveReq - A partial or complete move request object
   * @returns {object} Returns the modified version of validationInput
   */

  validateAndBuildInsertableMove: async (validationInput, moveReq = {}, options = {}) => {
    let validation = validationInput;

    // Get structured return object from the validation function
    validation = utilities.validateAndCleanseObject(moveReq, moveObjSchema(moveReq.schema, options));

    // Structured return object
    return validation;
  },

  /**
   * Validate and cleanse paired moves
   *
   * @param {object} validationInput - A structured validation object to edit and return
   * @param {array} movesReq - An array of partial or complete move request objects
   * @returns {string} Returns the modified version of validationInput
   */

  validateAndBuildInsertablePairedMoves: async (validationInput, movesReq = [{}]) => {
    let validation = validationInput;

    // Array of moveValidations that utilize the abstract validateAndCleanseObject() function
    const validationArray = movesReq.map((moveReq, i) =>
      utilities.validateAndCleanseObject(moveReq, moveObjSchema(moveReq.schema), `move ${i}`)
    );

    // Map over the validationArray and populate the new validation object
    // We do this so we have one structured object that we can check in the return (instead of an array)
    validationArray.map((mv, i) => {
      if (!mv.success) validation.success = false;
      if (mv.modified) validation.modified = true;
      if (mv.output) validation.output = [...validation.output, mv.output];
      if (mv.logs && mv.logs.length > 0) validation.logs = [...validation.logs, ...mv.logs];
      if (mv.errors && mv.errors.length > 0) validation.errors = [...validation.errors, ...mv.errors];
    });

    // Structured return object
    return validation;
  },

  //////////////////////////////////////// INSERTION FUNCTIONS ////////////////////////////////////////

  /**
   * Insert a single, validated move into the db
   *
   * @param {object} validationInput - A structured validation object to edit and return
   * @returns {object} Returns the modified version of validationInput
   */

  insertMove: async validationInput => {
    let validation = validationInput;

    // Insert the move into the db
    let movesRes = await moves.create([validation.output], { response_fragment: _parent.fragments.updaterMove() });
    log && console.log(`create() from insertMove() response:`, movesRes);

    // If moves insertion passes
    // Check for affected_rows field returned from gql.injectAffectedRowsAtRoot()
    if (movesRes && movesRes.data && movesRes.affected_rows === 1) {
      // Set validation output to the response data
      validation.output = movesRes.data[0];
    }
    // Else fail the validation
    else {
      validation.success = false;
      validation.errors.push(errors.buildApiError(errors.codes.INTERNAL_SERVER_ERROR));
      console.error(`insertMove() - Building of move has failed due to an internal server issue.`);
    }

    // Structured return object
    return validation;
  },

  /**
   * Link validated, paired moves by inserting them into the db and updating them (linked by return_ride_id)
   *
   * @param {object} validationInput - A structured validation object to edit and return
   * @param {boolean} [fromAPI=false] - An optional parameter to use a simplified move fragment when called using a user's JWT
   * @returns {string} Returns the modified version of validationInput
   */

  insertPairedMoves: async validationInput => {
    let validation = validationInput;

    // Insert the move into the db
    let movesRes = await moves.create(validation.output, { response_fragment: _parent.fragments.updaterMove() });
    log && console.log(`create() from insertPairedMoves() response:`, movesRes);

    // If moves insertion passes
    // Check for affected_rows field returned from gql.injectAffectedRowsAtRoot()
    if (movesRes && movesRes.data && movesRes.affected_rows === 2) {
      // Set validation output to the response data
      validation.output = movesRes.data;

      // Set moveId of second move to use parent_move_id of the first move
      let parentMoveId = movesRes.data[0].id;
      let moveId = movesRes.data[1].id;

      // If this update fails, which is a very rare edge case, we have already inserted the moves, which we cant undo
      // This means we can't fail the validation beyond this point

      // Update the second move in the sequence with the id of the first move as parent_move_id
      let moveUpdateRes = await moves.updateById(moveId, { parent_move_id: parentMoveId });
      log && console.log(`updateById() from insertPairedMoves() response:`, moveUpdateRes);
    }
    // Else fail the validation
    else {
      validation.success = false;
      validation.errors.push(errors.buildApiError(errors.codes.INTERNAL_SERVER_ERROR));
      console.error(`insertPairedMoves() - Building of move has failed due to an internal server issue.`);
    }

    // Structured return object
    return validation;
  },

  //////////////////////////////////////// BUILDING FUNCTIONS ////////////////////////////////////////

  /**
   * Validate/Insert a single one-way move
   *
   * @param {array} moveReq - A partial or complete move request object (API request must be converted first)
   * @returns {object} Structured return object that determines success or failure and logs events + gives input and output
   */

  buildOneWayMove: async (moveReq, options = {}) => {
    // Build a default validation object to return, this structure is based on the return structure from validateAndCleanseObject()
    let validation = utilities.defaultValidation(moveReq, {});

    try {
      // If the move request exists and is a valid object
      if (moveReq && moveReq === Object(moveReq)) {
        // Validate the move request and modify the return object
        validation = await moves.validateAndBuildInsertableMove(validation, moveReq, options);

        // If validation was successful, insert the move and modify return object
        if (validation.success) {
          validation = await moves.insertMove(validation, true);
        }
      }
      // Else fail the validation
      else {
        validation.success = false;
        validation.errors.push(errors.buildInvalidRequestError(errors.codes.REQUEST_INVALID));
        console.error(`buildOneWayMove() - Request needs to contain 'move'.`);
      }
    } catch (err) {
      validation.success = false;
      validation.errors.push(errors.buildApiError(errors.codes.UNKNOWN));
      console.error(`buildOneWayMove() - Building of move has failed due to an unexpected error:`, err);
    }

    // Structured return object
    return validation;
  },

  /**
   * Validate/Insert two paired (round-trip) moves
   *
   * @param {array} movesReq - An array of partial or complete move request objects (API request must be converted first)
   * @returns {object} Structured return object that determines success or failure and logs events + gives input and output
   */

  buildRoundTripMoves: async movesReq => {
    // Build new validation object to return, this structure is based on the return structure from validateAndCleanseObject()
    let validation = utilities.defaultValidation(movesReq, []);

    try {
      // Check to see if the move request array exists and has 2 moves
      if (Array.isArray(movesReq) && movesReq.length === 2) {
        // Map over the move requests and add the sequence field to both moves
        let newMovesReq = movesReq.map((moveReq, i) => {
          return { ...moveReq, sequence: i + 1 };
        });

        // Validate the moves request and modify the return object
        validation = await moves.validateAndBuildInsertablePairedMoves(validation, newMovesReq);

        // If validation was successful, insert the moves, then updateById to link by return_ride_id and modify return object
        if (validation.success) {
          validation = moves.insertPairedMoves(validation, true);
        }
      }
      // Else fail the validation
      else {
        validation.success = false;
        validation.errors.push(errors.buildInvalidRequestError(errors.codes.REQUEST_INVALID));
        console.error(`buildRoundTripMoves() - Request needs to contain 'move_one' and 'move_two'.`);
      }
    } catch (err) {
      validation.success = false;
      validation.errors.push(errors.buildApiError(errors.codes.UNKNOWN));
      console.error(`buildRoundTripMoves() - Building of move has failed due to an unexpected error:`, err);
    }

    // Structured return object
    return validation;
  },

  /**
   * Validate/Insert a single concierge move
   *
   * @param {array} moveReq - A partial or complete move request object (API request must be converted first)
   * @returns {object} Structured return object that determines success or failure and logs events + gives input and output
   */

  buildConciergeMove: async moveReq => {
    // Build a default validation object to return, this structure is based on the return structure from validateAndCleanseObject()
    let validation = utilities.defaultValidation(moveReq, {});

    try {
      // If the move request exists and is a valid object
      if (moveReq && moveReq === Object(moveReq)) {
        // Add schema field and edit ready_by time if necessary
        let newMoveReq = { ...moveReq };
        newMoveReq.schema = `concierge`;

        // Validate the move request and modify the return object
        validation = await moves.validateAndBuildInsertableMove(validation, newMoveReq);

        // If validation was successful, insert the move and modify return object
        if (validation.success) {
          validation = moves.insertMove(validation, true);
        }
      }
      // Else fail the validation
      else {
        validation.success = false;
        validation.errors.push(errors.buildInvalidRequestError(errors.codes.REQUEST_INVALID));
        console.error(`buildConciergeMove() - Request needs to contain 'customer_move'.`);
      }
    } catch (err) {
      validation.success = false;
      validation.errors.push(errors.buildApiError(errors.codes.UNKNOWN));
      console.error(`buildConciergeMove() - Building of move has failed due to an unexpected error:`, err);
    }

    // Structured return object
    return validation;
  },

  /**
   * Validate/Insert two paired (loaner+concierge) moves
   *
   * @param {array} movesReq - An array of partial or complete move request objects (API request must be converted first)
   * @returns {object} Structured return object that determines success or failure and logs events + gives input and output
   */

  buildConciergeLoanerMoves: async (movesReq, customerFirst = false) => {
    // Build new validation object to return, this structure is based on the return structure from validateAndCleanseObject()
    let validation = utilities.defaultValidation(movesReq, []);

    try {
      // Check to see if the move request array exists and has 2 moves
      if (Array.isArray(movesReq) && movesReq.length === 2) {
        let newMovesReq = [];

        // Map over the move requests and add fields to the loaner and concierge moves
        newMovesReq = movesReq.map((moveReq, i) => {
          // Add sequence, stranded class and override
          let newMoveReq = { ...moveReq, sequence: i + 1, class: `stranded`, rate_class_override: 1 };

          // Set loaner and concierge moves to make sure the request is valid
          if (!customerFirst) {
            if (i === 0) {
              newMoveReq.consumer_at_pickup = 0;
              newMoveReq.schema = `loaner`; // Use loaner schema to validate
            }
            if (i === 1) {
              newMoveReq.consumer_at_pickup = 1;
              newMoveReq.schema = `concierge`; // Use concierge schema to validate
            }
          } else {
            if (i === 0) {
              newMoveReq.consumer_at_pickup = 0;
              newMoveReq.schema = `concierge`; // Use concierge schema to validate
            }
            if (i === 1) {
              newMoveReq.consumer_at_pickup = 1;
              newMoveReq.schema = `loaner`; // Use loaner schema to validate
            }
          }

          return newMoveReq;
        });

        // Validate the moves request and modify the return object
        validation = await moves.validateAndBuildInsertablePairedMoves(validation, newMovesReq);

        // If validation was successful, insert the moves, then updateById to link by return_ride_id and modify return object
        if (validation.success) {
          validation = moves.insertPairedMoves(validation, true);
        }
      }
      // Else fail the validation
      else {
        validation.success = false;
        validation.errors.push(errors.buildInvalidRequestError(errors.codes.REQUEST_INVALID));
        console.error(`buildConciergeLoanerMoves() - Request needs to contain 'loaner_move' and 'customer_move'.`);
      }
    } catch (err) {
      validation.success = false;
      validation.errors.push(errors.buildApiError(errors.codes.UNKNOWN));
      console.error(`buildConciergeLoanerMoves() - Building of move has failed due to an unexpected error:`, err);
    }

    // Structured return object
    return validation;
  },

  ////////////////////////////////////// API ORCHESTRATION FUNCTIONS //////////////////////////////////////

  /**
   * Validate the move request object for a single move
   *
   * @param {Object} moveReq - The move request received by the API
   * @param {boolean} isConcierge - Boolean to determine if its a concierge move
   * @returns {Object} An object with a success boolean and an errors array
   */

  validateMoveRequest: (moveReq, options = { isGhost: false, isConcierge: false, isConciergePickup: false }) => {
    //If Concierge or Concierge Pickup, the first schema validation step will be different
    const { isGhost = false, isConcierge = false, isConciergePickup = false } = options || {};
    let schemaType = isConcierge ? 'concierge' : null;

    // Separate each part of the request because the validation function cant handle nested objects
    let req = moveReq || {};
    let reqMove = (!isConcierge ? req.move : req.customer_move) || {};
    let reqVehicle = reqMove.vehicle || {};
    let reqPickup = reqMove.pickup_location || {};
    let reqDelivery = reqMove.delivery_location || {};
    let reqConfig = reqMove.config || {};

    // Check if full address is provided for pickup and delivery
    const isFullPickupAddressProvided = reqPickup.full_address ? true : false; 
    const isFullDeliveryAddressProvided = reqDelivery.full_address ? true : false;

    // Validate the different parts of the request object
    // Create an array of each of the individual validation objects
    let validationArray = [
      _parent.utilities.validateAndCleanseObject(req, requestSchema(schemaType, { isConciergePickup })),
      _parent.utilities.validateAndCleanseObject(reqMove, moveSchema()),
      _parent.utilities.validateAndCleanseObject(reqVehicle, vehicleSchema(isGhost ? `loaner` : null)),
      _parent.utilities.validateAndCleanseObject(reqPickup, locationSchema(schemaType, isFullPickupAddressProvided)),
      _parent.utilities.validateAndCleanseObject(
        reqDelivery,
        locationSchema(schemaType, isFullDeliveryAddressProvided)
      ),
      _parent.utilities.validateAndCleanseObject(reqConfig, configSchema()),
    ];

    // Filter only failures and return all the errors
    // If there are any errors, we know the validation failed
    const errorArray = validationArray
      .filter(vd => vd.success === false)
      .map(vd => vd.errors)
      .flat();

    // Determine success based on if there are any errors
    if (errorArray.length > 0) return { success: false, errors: errorArray };
    else return { success: true };
  },

  /**
   * Validate the move request object for paired moves
   *
   * @param {Object} moveReq - The move request received by the API
   * @param {boolean} isConcierge - Boolean to determine if its a concierge+loaner move
   * @param {boolean} customerFirst - Boolean to determine if concierge move is first
   * @returns {Object} An object with a success boolean and an errors array
   */

  validatePairedMoveRequest: (moveReq, isConcierge = false, customerFirst = false) => {
    // Separate each part of the request because the validation function cant handle nested objects
    let req = moveReq || {};
    let reqMove1 = (!isConcierge ? req.move_one : !customerFirst ? req.loaner_move : req.customer_move) || {};
    let reqVehicle1 = reqMove1.vehicle || {};
    let reqPickup1 = reqMove1.pickup_location || {};
    let reqConfig1 = reqMove1.config || {};
    let reqMove2 = (!isConcierge ? req.move_two : !customerFirst ? req.customer_move : req.loaner_move) || {};
    let reqVehicle2 = reqMove2.vehicle || {};
    let reqPickup2 = reqMove2.pickup_location || {};
    let reqConfig2 = reqMove2.config || {};

    //If Concierge, the first schema validation step will be different
    let schemaType = isConcierge ? 'concierge' : null;

    // Check if full address is provided for pickup and delivery
    const isFullPickup1AddressProvided = reqPickup1.full_address ? true : false;
    const isFullPickup2AddressProvided = reqPickup2.full_address ? true : false;

    //Loaner has a different validation, so we need to check if the move is a loaner move
    let vehicleOneType = null;
    let vehicleTwoType = null;
    if (isConcierge && customerFirst) {
      vehicleTwoType = 'loaner';
    } else if (isConcierge && !customerFirst) {
      vehicleOneType = 'loaner';
    }

    // Validate the different parts of the request object
    // Create an array of each of the individual validation objects
    let validationArray = [
      _parent.utilities.validateAndCleanseObject(req, requestSchema(schemaType)),
      _parent.utilities.validateAndCleanseObject(reqMove1, moveSchema()),
      _parent.utilities.validateAndCleanseObject(reqVehicle1, vehicleSchema(vehicleOneType)),
      _parent.utilities.validateAndCleanseObject(reqPickup1, locationSchema(schemaType, isFullPickup1AddressProvided)),
      _parent.utilities.validateAndCleanseObject(reqConfig1, configSchema()),
      _parent.utilities.validateAndCleanseObject(reqMove2, moveSchema()),
      _parent.utilities.validateAndCleanseObject(reqVehicle2, vehicleSchema(vehicleTwoType)),
      _parent.utilities.validateAndCleanseObject(reqPickup2, locationSchema(schemaType, isFullPickup2AddressProvided)),
      _parent.utilities.validateAndCleanseObject(reqConfig2, configSchema()),
    ];

    // Filter only failures and return all the errors
    // If there are any errors, we know the validation failed
    const errorArray = validationArray
      .filter(vd => vd.success === false)
      .map(vd => vd.errors)
      .flat();

    // Determine success based on if there are any errors
    if (errorArray.length > 0) return { success: false, errors: errorArray };
    else return { success: true };
  },

  /** Check for duplicate moves in-progress */
  checkDupeRequest: async function (customerId, dupeField, move) {
    if (customerId && dupeField && move) {
      let dupeValue = null;
      if (dupeField === `reference_num`) dupeValue = getPropValue(move, `reference_num`);
      if (dupeField === `vehicle_stock`) dupeValue = getPropValue(move, `vehicle.stock`);
      if (dupeField === `vehicle_vin`) dupeValue = getPropValue(move, `vehicle.vin`);

      try {
        let dupeRes = await _parent.moves.getDupes(customerId, dupeField, dupeValue);
        log && console.log(`Check for dupes response:`, dupeRes);
        if (dupeRes.success && dupeRes.data && dupeRes.data.length > 0) return true;
      } catch (err) {
        console.error(`Unexpected error checking for duplicates:`, err);
        return false;
      }
    }
    return false;
  },

  /** Check for duplicate paired moves in-progress */
  checkPairedDupeRequest: async function (customerId, dupeField, moveOne, moveTwo) {
    if (customerId && dupeField && moveOne && moveTwo) {
      let dupeValueOne = null;
      let dupeValueTwo = null;
      if (dupeField === `reference_num`) {
        dupeValueOne = getPropValue(moveOne, `reference_num`);
        dupeValueTwo = getPropValue(moveTwo, `reference_num`);
      }
      if (dupeField === `vehicle_stock`) {
        dupeValueOne = getPropValue(moveOne, `vehicle.stock`);
        dupeValueTwo = getPropValue(moveTwo, `vehicle.stock`);
      }
      if (dupeField === `vehicle_vin`) {
        dupeValueOne = getPropValue(moveOne, `vehicle.vin`);
        dupeValueTwo = getPropValue(moveTwo, `vehicle.vin`);
      }

      try {
        let dupeRes = await _parent.moves.getPairedDupes(customerId, dupeField, dupeValueOne, dupeValueTwo);
        log && console.log(`Check for paired dupes response:`, dupeRes);
        if (dupeRes.success && dupeRes.data && dupeRes.data.length > 0) return true;
      } catch (err) {
        console.error(`Unexpected error checking for paired duplicates:`, err);
        return false;
      }
    }
    return false;
  },

  /** Find workflowset by looking at either the move request, or customer config. Fallback to default */
  findWorkflowset: async function (moveReq, customerId, isConcierge = false) {
    // Initialize the returnable workflowset and workflowsetId to query
    let workflowsetId = null;
    const moveTime = isConcierge ? moveReq.appointment_time_utc : moveReq.ready_by_time_utc;

    // Determine move object based on concierge bool
    const move = isConcierge ? moveReq.customer_move : moveReq.move;

    // TODO: Remove this once Acertus is fully integrated
    const acertusCheck = await this.checkAcertus(moveReq);

    if (acertusCheck && acertusCheck.data && acertusCheck.data.length) {
      return acertusCheck.data[0];
    }

    // Assign workflowsetId if it's on the move
    if (move.workflowset_id) {
      workflowsetId = move.workflowset_id;
    }
    if (workflowsetId) {
      const workflowsetRes = await this.getWorkflowset(workflowsetId, customerId, moveTime);
      if (workflowsetRes.data && workflowsetRes.data.length) {
        return workflowsetRes.data[0];
      }
    }
    try {
      const customerConfig = await _parent.configs.getCustomerConfig(customerId);
      if (customerConfig.default_workflow_set_id) {
        const workflowsetRes = await this.getWorkflowset(customerConfig.default_workflow_set_id, customerId, moveTime);
        if (workflowsetRes.data && workflowsetRes.data.length) {
          return workflowsetRes.data[0];
        }
      }
    } catch (err) {
      console.warn(`Failed to get customer config. Falling back to default workflowset...`);
    }

    // If we still dont have a workflowset, fallback to default
    return defaultWorkflowset;
  },

  /** Find paired workflowsets by looking at either the move request, or customer config. Fallback to default */
  findPairedWorkflowsets: async function (moveReq, customerId, isConcierge = false, customerFirst = false) {
    // Initialize the returnable workflowset and workflowsetId to query
    let workflowset1 = null;
    let workflowset2 = null;

    let workflowsetId1 = null;
    let workflowsetId2 = null;

    const moveTime = isConcierge ? moveReq.appointment_time_utc : moveReq.ready_by_time_utc;

    // Determine move objects based on concierge and customer first bool
    const move1 = getPropValue(
      moveReq,
      `${!isConcierge ? `move_one` : customerFirst ? `customer_move` : `loaner_move`}`
    );
    const move2 = getPropValue(
      moveReq,
      `${!isConcierge ? `move_two` : customerFirst ? `loaner_move` : `customer_move`}`
    );

    // Assign workflowsetId if it's on the move
    if (move1.workflowset_id) workflowsetId1 = move1.workflowset_id;
    if (move2.workflowset_id) workflowsetId2 = move2.workflowset_id;

    // If we have a workflowsetId, make sure the user has access by testing the permissions
    if (workflowsetId1 && workflowsetId2) {
      const workflowsetRes = await this.getPairedWorkflowsets(workflowsetId1, workflowsetId2, moveTime);
      if (
        workflowsetRes.data &&
        workflowsetRes.data.workflowsets1 &&
        workflowsetRes.data.workflowsets2 &&
        workflowsetRes.data.workflowsets1.length &&
        workflowsetRes.data.workflowsets2.length
      ) {
        workflowset1 = workflowsetRes.data.workflowsets1[0];
        workflowset2 = workflowsetRes.data.workflowsets2[0];
      }
    } else if (workflowsetId1) {
      const workflowset1Res = await this.getWorkflowset(workflowsetId1, customerId, moveTime);
      if (workflowset1Res.data && workflowset1Res.data.length) workflowset1 = workflowset1Res.data[0];
    } else if (workflowsetId2) {
      const workflowset2Res = await this.getWorkflowset(workflowsetId2, customerId, move2.ready_by);
      if (workflowset2Res.data && workflowset2Res.data.length) workflowset2 = workflowset2Res.data[0];
    }

    // If theres still no workflowset, look at the customer config for a default
    if (!workflowset1 || !workflowset2) {
      try {
        const customerConfig = await _parent.configs.getCustomerConfig(customerId);
        if (customerConfig.default_workflow_set_id) {
          const workflowset2Res = await this.getWorkflowset(
            customerConfig.default_workflow_set_id,
            customerId,
            moveTime
          );
          if (workflowset2Res.data && workflowset2Res.data.length) {
            if (!workflowset1) {
              workflowset1 = workflowset2Res.data[0];
            }
            if (!workflowset2) {
              workflowset2 = workflowset2Res.data[0];
            }
          }
        }
      } catch (err) {
        console.warn(`Failed to get customer config. Falling back to default workflowset...`);
      }
    }

    // If we still dont have a workflowset, fallback to default
    if (!workflowset1) workflowset1 = defaultWorkflowset;
    if (!workflowset2) workflowset2 = defaultWorkflowset;

    // Return the workflowsets
    return [workflowset1, workflowset2];
  },

  /** Find SLA by looking at the move request. Fallback to default */
  findSla: async function (moveReq, customerId, workflowSet, isConcierge = false) {
    // Initialize the returnable SLA
    let sla = null;
    // If this is a concierge move, ALWAYS override with the default concierge SLA
    if (isConcierge) {
      try {
        const resConciergeSla = await this.getConciergeSla();
        if (resConciergeSla && resConciergeSla.data && resConciergeSla.data.length) {
          return resConciergeSla.data[0];
        }
      } catch (err) {
        console.warn(`Failed to get concierge SLA. Falling back to hardcoded SLA...`);
        return conciergeSla;
      }
    }
    // get SLA from workflow's current raterulegroup
    if (
      workflowSet &&
      workflowSet.raterulegroups &&
      workflowSet.raterulegroups.length &&
      workflowSet.raterulegroups[0].sla
    ) {
      return workflowSet.raterulegroups[0].sla;
    }

    // If we still don't have a sla, fallback to company default
    if (!sla) {
      try {
        const resDefaultSla = await this.getDefaultSla();
        if (resDefaultSla && resDefaultSla.data && resDefaultSla.data.length) {
          return resDefaultSla.data[0];
        }
      } catch (err) {
        console.warn(`Failed to get default SLA. Falling back to hardcoded SLA...`);
      }
    }

    return defaultSla;
  },

  /** Find paired SLAs by looking at either the move request, or customer config. Fallback to default */
  findPairedSlas: async function (moveReq, customerId, workflowsets, isConcierge = false, customerFirst = false) {
    // Initialize the returnable SLA
    let sla1 = null;
    let sla2 = null;

    // Determine move objects based on concierge and customer first bool
    const move1 = getPropValue(
      moveReq,
      `${!isConcierge ? `move_one` : customerFirst ? `customer_move` : `loaner_move`}`
    );
    const move2 = getPropValue(
      moveReq,
      `${!isConcierge ? `move_two` : customerFirst ? `loaner_move` : `customer_move`}`
    );

    // If this is a concierge move, ALWAYS override with the default concierge SLA
    if (isConcierge) {
      try {
        const resConciergeSla = await this.getConciergeSla();
        if (resConciergeSla && resConciergeSla.data && resConciergeSla.data.length) {
          sla1 = resConciergeSla.data[0];
          sla2 = resConciergeSla.data[0];
        }
      } catch (err) {
        console.warn(`Failed to get concierge SLA. Falling back to hardcoded SLA...`);
        sla1 = conciergeSla;
        sla2 = conciergeSla;
      }
      return [sla1, sla2];
    }

    // get SLA from workflow sets' raterulegroups if present
    if (
      workflowsets &&
      workflowsets.length === 2 &&
      workflowsets[0].raterulegroups &&
      workflowsets[0].raterulegroups.length &&
      workflowsets[0].raterulegroups[0].sla &&
      workflowsets[1].raterulegroups &&
      workflowsets[1].raterulegroups.length &&
      workflowsets[1].raterulegroups[0].sla
    ) {
      sla1 = workflowsets[0].raterulegroups[0].sla;
      sla2 = workflowsets[1].raterulegroups[0].sla;
      return [sla1, sla2];
    }

    // If we still don't have a sla, fallback to company default
    try {
      const resDefaultSla = await this.getDefaultSla();
      if (resDefaultSla && resDefaultSla.data && resDefaultSla.data.length) {
        return [resDefaultSla.data[0], resDefaultSla.data[0]];
      }
    } catch (err) {
      console.warn(`Failed to get default SLA. Falling back to hardcoded SLA...`);
    }

    // Return hardcoded default sla
    return [defaultSla, defaultSla];
  },

  /**
   * Creates a one way move plus associated locations/lane (if needed)
   *
   * @param {Object} moveReq - The move request recieved by the API to create a one-way move
   * @param {Object} customerId - the customerId, as parsed from the auth0 JWT
   * @returns {Object} The Axios response object from the GET
   */

  createOneWayMove: async function (moveReq, customerId, options = {}) {
    // First pass validation: Validate the request object
    let validation = this.validateMoveRequest(moveReq, false, options);

    // If validation fails we return an array of error objects
    if (validation.success === false) return { errors: validation.errors };

    // Check for move duplication based on field passed in
    if (moveReq.duplicate_check) {
      let isDupe = await this.checkDupeRequest(customerId, moveReq.duplicate_check, getPropValue(moveReq, `move`));
      if (isDupe === true) {
        return { errors: [_parent.errors.buildValidationError(_parent.errors.codes.MOVE_DUPLICATION)] };
      }
    }

    // Query and check if the user has access to the requested workflowset or fallback to the default
    const workflowset = await _parent.moves.findWorkflowset(moveReq, customerId);

    // Query and check if the user has access to the requested SLA or fallback to the default
    const sla = await _parent.moves.findSla(moveReq, customerId, workflowset);

    // Fetch customer config to check if there is a default payer
    try {
      const customerConfig = await _parent.configs.getCustomerConfig(customerId);
      const defaultPayer = customerConfig && customerConfig.default_payer ? customerConfig.default_payer : null;
      const thirdPartyRequired =
        customerConfig && customerConfig.require_third_party_payer ? customerConfig.require_third_party_payer : false;
      if (thirdPartyRequired && !moveReq.third_party_payer && defaultPayer && defaultPayer !== customerId)
        moveReq.third_party_payer = defaultPayer;
    } catch (error) {
      console.error('Failed to fetch customer config', error);
      return {
        success: false,
        customer_id: null,
        errors: [errors.buildApiError(errors.codes.INTERNAL_SERVER_ERROR, 'Could not fetch customer config')],
      };
    }

    // Define pickup and delivery
    const pickup = getPropValue(moveReq, 'move.pickup_location');
    const delivery = getPropValue(moveReq, 'move.delivery_location');

    const pickupAddress = _parent.locations.buildAddress(
      pickup.address_one,
      pickup.city,
      pickup.state,
      pickup.zip,
      pickup.address_two,
      pickup.full_address
    );
    const deliveryAddress = _parent.locations.buildAddress(
      delivery.address_one,
      delivery.city,
      delivery.state,
      delivery.zip,
      delivery.address_two,
      delivery.full_address
    );

    let pickupName = pickup.name;
    if (!pickupName) {
      if (pickup.full_address) pickupName = _parent.locations.defaultLocationName(pickup.full_address);
      else if (pickup.address_one && pickup.address_two)
        pickupName = _parent.locations.defaultLocationName(`${pickup.address_one} ${pickup.address_two}`);
      else if (pickup.address_one) pickupName = _parent.locations.defaultLocationName(pickup.address_one);
    }

    let deliveryName = delivery.name;
    if (!deliveryName) {
      if (delivery.full_address) deliveryName = _parent.locations.defaultLocationName(delivery.full_address);
      else if (delivery.address_one && delivery.address_two)
        deliveryName = _parent.locations.defaultLocationName(`${delivery.address_one} ${delivery.address_two}`);
      else if (delivery.address_one) deliveryName = _parent.locations.defaultLocationName(delivery.address_one);
    }

    log && console.log('pickupAddress', pickupAddress, deliveryAddress, pickupName, deliveryName);

    // Query lane or insert shell lane
    const lanes = await _parent.lanes.getLaneOrInsertShell(
      customerId,
      pickupName,
      deliveryName,
      pickupAddress,
      deliveryAddress,
      options
    );
    log && console.log('Lanes:', lanes);

    // Look for errors in the lane creation and return them
    if (lanes.errors) {
      return { errors: lanes.errors };
    }

    // Check that at least one of the locations on the new lane have a live region
    const laneOne = lanes[0];
    const pickupRegionStatus = laneOne && laneOne.pickup && laneOne.pickup.region && laneOne.pickup.region.status;
    const deliveryRegionStatus = laneOne && laneOne.delivery && laneOne.delivery.region && laneOne.delivery.region.status;
    if (pickupRegionStatus !== 'live' && deliveryRegionStatus !== 'live') {
      return { errors: [errors.codes.OUTSIDE_REGION] };
    }

    // Convert move request to our DB structure
    let convertedMoveReq = _parent.moves.convertMoveToDBSchema(moveReq, workflowset, sla);

    // Add customer and lane id to converted request
    convertedMoveReq.customer_id = customerId;
    convertedMoveReq.lane_id = lanes[0].id;

    // Build based on the converted move request
    const buildMoveRes = await _parent.moves.buildOneWayMove(convertedMoveReq, options);

    // Set response object
    let moveRes = {};

    // If returned validation from buildMoveRes passes, then convert the buildMoveRes output to the API response structure
    // If returned validation from buildMoveRes fails, then return the errors
    if (buildMoveRes.success === true) moveRes = _parent.moves.convertMoveToAPISchema(buildMoveRes.output);
    else moveRes = { errors: buildMoveRes.errors };

    // TBD - Check the moveRes; process and return either error or success
    return moveRes;
  },

  /**
   * Creates roundtrip pair of moves plus associated locations/lane (if needed)
   *
   * @param {Object} moveReq - The move request recieved by the API to create a one-way move
   * @param {Object} customerId - the customerId, as parsed from the auth0 JWT
   * @returns {Object} The Axios response object from the GET
   */

  createRoundTripMoves: async function (moveReq, customerId, options = {}) {
    // First pass validation: Validate the request object
    let validation = this.validatePairedMoveRequest(moveReq);

    // If validation fails we return an array of error objects
    if (validation.success === false) return { errors: validation.errors };

    // Check for move duplication based on field passed in
    if (moveReq.duplicate_check) {
      let isDupe = await this.checkPairedDupeRequest(
        customerId,
        moveReq.duplicate_check,
        getPropValue(moveReq, `move_one`),
        getPropValue(moveReq, `move_two`)
      );
      if (isDupe === true) {
        return { errors: [_parent.errors.buildValidationError(_parent.errors.codes.MOVE_DUPLICATION)] };
      }
    }

    // Query and check if the user has access to the requested workflowset or fallback to the default;
    const workflowset1 = await _parent.moves.findWorkflowset(
      {
        rooftop: moveReq.rooftop,
        move: moveReq.move_one,
      },
      customerId
    );

    const workflowset2 = await _parent.moves.findWorkflowset(
      {
        rooftop: moveReq.rooftop,
        move: moveReq.move_two,
      },
      customerId
    );

    const workflowsets = [workflowset1, workflowset2];

    // Query and check if the user has access to the requested SLA or fallback to the default
    const slas = await _parent.moves.findPairedSlas(moveReq, customerId, workflowsets);

    // Fetch customer config to check if there is a default payer
    try {
      const customerConfig = await _parent.configs.getCustomerConfig(customerId);
      const defaultPayer = customerConfig && customerConfig.default_payer ? customerConfig.default_payer : null;
      const thirdPartyRequired =
        customerConfig && customerConfig.require_third_party_payer ? customerConfig.require_third_party_payer : false;
      if (thirdPartyRequired && !moveReq.third_party_payer && defaultPayer && defaultPayer !== customerId)
        moveReq.third_party_payer = defaultPayer;
    } catch (error) {
      console.error('Failed to fetch customer config', error);
      return {
        success: false,
        customer_id: null,
        errors: [errors.buildApiError(errors.codes.INTERNAL_SERVER_ERROR, 'Could not fetch customer config')],
      };
    }

    // Define pickup and delivery
    const pickup = getPropValue(moveReq, 'move_one.pickup_location');
    const delivery = getPropValue(moveReq, 'move_two.pickup_location');

    const pickupAddress = _parent.locations.buildAddress(
      pickup.address_one,
      pickup.city,
      pickup.state,
      pickup.zip,
      pickup.address_two,
      pickup.full_address
    );
    const deliveryAddress = _parent.locations.buildAddress(
      delivery.address_one,
      delivery.city,
      delivery.state,
      delivery.zip,
      delivery.address_two,
      delivery.full_address
    );

    let pickupName = pickup.name;
    if (!pickupName) {
      if (pickup.full_address) pickupName = _parent.locations.defaultLocationName(pickup.full_address);
      else if (pickup.address_one && pickup.address_two)
        pickupName = _parent.locations.defaultLocationName(`${pickup.address_one} ${pickup.address_two}`);
      else if (pickup.address_one) pickupName = _parent.locations.defaultLocationName(pickup.address_one);
    }

    let deliveryName = delivery.name;
    if (!deliveryName) {
      if (delivery.full_address) deliveryName = _parent.locations.defaultLocationName(delivery.full_address);
      else if (delivery.address_one && delivery.address_two)
        deliveryName = _parent.locations.defaultLocationName(`${delivery.address_one} ${delivery.address_two}`);
      else if (delivery.address_one) deliveryName = _parent.locations.defaultLocationName(delivery.address_one);
    }

    log && console.log('pickupAddress', pickupAddress, deliveryAddress, pickupName, deliveryName);

    // Query lane or insert shell lane
    const lanes = await _parent.lanes.getLaneOrInsertShell(
      customerId,
      pickupName,
      deliveryName,
      pickupAddress,
      deliveryAddress,
      options
    );
    log && console.log('Lanes:', lanes);

    // Look for errors in the lane creation and return them
    if (lanes.errors) {
      return { errors: lanes.errors };
    }

    // Check that at least one of the locations on the new lane have a live region
    const laneOne = lanes[0];
    const pickupRegionStatus = laneOne && laneOne.pickup && laneOne.pickup.region && laneOne.pickup.region.status;
    const deliveryRegionStatus = laneOne && laneOne.delivery && laneOne.delivery.region && laneOne.delivery.region.status;
    if (pickupRegionStatus !== 'live' && deliveryRegionStatus !== 'live') {
      return { errors: [errors.codes.OUTSIDE_REGION] };
    }

    // Convert move request to our DB structure
    let convertedMoveReq = _parent.moves.convertPairedMovesToDBSchema(moveReq, workflowsets, slas);

    // Add customer and lane id to converted request
    convertedMoveReq[0].customer_id = customerId;
    convertedMoveReq[0].lane_id = lanes[0].id;
    convertedMoveReq[1].customer_id = customerId;
    convertedMoveReq[1].lane_id = lanes[1].id;

    // Build based on the converted move request
    const buildMoveRes = await _parent.moves.buildRoundTripMoves(convertedMoveReq);

    // Set response object
    let moveRes = {};

    // If returned validation from buildMoveRes passes, then convert the buildMoveRes output to the API response structure
    // If returned validation from buildMoveRes fails, then return the errors
    if (buildMoveRes.success === true) moveRes = _parent.moves.convertPairedMovesToAPISchema(buildMoveRes.output);
    else moveRes = { errors: buildMoveRes.errors };

    // TBD - Check the moveRes; process and return either error or success
    return moveRes;
  },

  /**
   * Creates a concierge move plus associated locations/lane (if needed)
   *
   * @param {Object} moveReq - The move request recieved by the API to create a one-way move
   * @param {Object} customerId - the customerId, as parsed from the auth0 JWT
   * @returns {Object} The Axios response object from the GET
   */

  createConciergeMove: async function (moveReq, customerId, options = {}) {
    // Determine if the move is concierge
    const isConcierge = true;
    const isConciergePickup = moveReq.customer_action === `pickup`;

    // First pass validation: Validate the request object
    let validation = this.validateMoveRequest(moveReq, { isConcierge: isConcierge, isConciergePickup: isConciergePickup });

    // If validation fails we return an array of error objects
    if (validation.success === false) return { errors: validation.errors };

    // Check for move duplication based on field passed in
    if (moveReq.duplicate_check) {
      let isDupe = await this.checkDupeRequest(
        customerId,
        moveReq.duplicate_check,
        getPropValue(moveReq, `customer_move`)
      );
      if (isDupe === true) {
        return { errors: [_parent.errors.buildValidationError(_parent.errors.codes.MOVE_DUPLICATION)] };
      }
    }

    // Query and check if the user has access to the requested workflowset or fallback to the default
    const workflowset = await _parent.moves.findWorkflowset(moveReq, customerId, isConcierge);

    // Query and check if the user has access to the requested SLA or fallback to the default
    const sla = await _parent.moves.findSla(moveReq, customerId, workflowset, isConcierge);

    // Fetch customer config to check if there is a default payer
    try {
      const customerConfig = await _parent.configs.getCustomerConfig(customerId);
      const defaultPayer = customerConfig && customerConfig.default_payer ? customerConfig.default_payer : null;
      const thirdPartyRequired =
        customerConfig && customerConfig.require_third_party_payer ? customerConfig.require_third_party_payer : false;
      if (thirdPartyRequired && !moveReq.third_party_payer && defaultPayer && defaultPayer !== customerId)
        moveReq.third_party_payer = defaultPayer;
    } catch (error) {
      console.error('Failed to fetch customer config', error);
      return {
        success: false,
        customer_id: null,
        errors: [errors.buildApiError(errors.codes.INTERNAL_SERVER_ERROR, 'Could not fetch customer config')],
      };
    }

    // Define pickup and delivery
    const pickup = getPropValue(moveReq, 'customer_move.pickup_location');
    const delivery = getPropValue(moveReq, 'customer_move.delivery_location');

    const pickupAddress = _parent.locations.buildAddress(
      pickup.address_one,
      pickup.city,
      pickup.state,
      pickup.zip,
      pickup.address_two,
      pickup.full_address
    );
    const deliveryAddress = _parent.locations.buildAddress(
      delivery.address_one,
      delivery.city,
      delivery.state,
      delivery.zip,
      delivery.address_two,
      delivery.full_address
    );

    let pickupName = pickup.name;
    if (!pickupName) {
      if (pickup.full_address) pickupName = _parent.locations.defaultLocationName(pickup.full_address);
      else if (pickup.address_one && pickup.address_two)
        pickupName = _parent.locations.defaultLocationName(`${pickup.address_one} ${pickup.address_two}`);
      else if (pickup.address_one) pickupName = _parent.locations.defaultLocationName(pickup.address_one);
    }

    let deliveryName = delivery.name;
    if (!deliveryName) {
      if (delivery.full_address) deliveryName = _parent.locations.defaultLocationName(delivery.full_address);
      else if (delivery.address_one && delivery.address_two)
        deliveryName = _parent.locations.defaultLocationName(`${delivery.address_one} ${delivery.address_two}`);
      else if (delivery.address_one) deliveryName = _parent.locations.defaultLocationName(delivery.address_one);
    }

    log && console.log('pickupAddress', pickupAddress, deliveryAddress, pickupName, deliveryName);

    // Query lane or insert shell lane
    const lanes = await _parent.lanes.getLaneOrInsertShell(
      customerId,
      pickupName,
      deliveryName,
      pickupAddress,
      deliveryAddress,
      options
    );
    log && console.log('Lanes:', lanes);

    // Look for errors in the lane creation and return them
    if (lanes.errors) {
      return { errors: lanes.errors };
    }

    // Check that at least one of the locations on the new lane have a live region
    const laneOne = lanes[0];
    const pickupRegionStatus = laneOne && laneOne.pickup && laneOne.pickup.region && laneOne.pickup.region.status;
    const deliveryRegionStatus = laneOne && laneOne.delivery && laneOne.delivery.region && laneOne.delivery.region.status;
    if (pickupRegionStatus !== 'live' && deliveryRegionStatus !== 'live') {
      return { errors: [errors.codes.OUTSIDE_REGION] };
    }

    //We need to add the lane duration to get the deliver by time for concierge
    const laneDuration = laneOne.duration_sec;

    // Convert move request to our DB structure
    let convertedMoveReq = _parent.moves.convertMoveToDBSchema(moveReq, workflowset, sla, isConcierge);

    // Add customer and lane id to converted request
    convertedMoveReq.customer_id = customerId;
    convertedMoveReq.lane_id = lanes[0].id;

    // Build based on the converted move request
    const buildMoveRes = await _parent.moves.buildConciergeMove(convertedMoveReq);

    // Set response object
    let moveRes = {};

    // If returned validation from buildMoveRes passes, then convert the buildMoveRes output to the API response structure
    // If returned validation from buildMoveRes fails, then return the errors
    if (buildMoveRes.success === true) moveRes = _parent.moves.convertMoveToAPISchema(buildMoveRes.output, isConcierge);
    else moveRes = { errors: buildMoveRes.errors };

    // TBD - Check the moveRes; process and return either error or success
    return moveRes;
  },

  /**
   * Creates a concierge and loaner move pair plus associated locations/lane (if needed)
   *
   * @param {Object} moveReq - The move request recieved by the API to create a one-way move
   * @param {Object} customerId - the customerId, as parsed from the auth0 JWT
   * @returns {Object} The Axios response object from the GET
   */

  createConciergeLoanerMoves: async function (moveReq, customerId, options = {}) {
    // Determine if the move is concierge and/or concierge-first
    const isConcierge = true;
    const customerFirst = moveReq.customer_action === `pickup` ? false : true;

    // First pass validation: Validate the request object
    let validation = this.validatePairedMoveRequest(moveReq, isConcierge, customerFirst);

    // If validation fails we return an array of error objects
    if (validation.success === false) return { errors: validation.errors };

    // Check for move duplication based on field passed in
    if (moveReq.duplicate_check) {
      let isDupe = await this.checkPairedDupeRequest(
        customerId,
        moveReq.duplicate_check,
        getPropValue(moveReq, `loaner_move`),
        getPropValue(moveReq, `customer_move`)
      );
      if (isDupe === true) {
        return { errors: [_parent.errors.buildValidationError(_parent.errors.codes.MOVE_DUPLICATION)] };
      }
    }

    // Query and check if the user has access to the requested workflowset or fallback to the default
    const move1 = moveReq.move_one || (customerFirst ? moveReq.customer_move : moveReq.loaner_move);
    const move2 = moveReq.move_two || (customerFirst ? moveReq.loaner_move : moveReq.customer_move);

    const workflowset1 = await _parent.moves.findWorkflowset(
      {
        rooftop: moveReq.rooftop,
        move: move1,
      },
      customerId
    );

    const workflowset2 = await _parent.moves.findWorkflowset(
      {
        rooftop: moveReq.rooftop,
        move: move2,
      },
      customerId
    );

    const workflowsets = [workflowset1, workflowset2];

    // Query and check if the user has access to the requested SLA or fallback to the default
    const slas = await _parent.moves.findPairedSlas(moveReq, customerId, workflowsets, isConcierge, customerFirst);

    // Fetch customer config to check if there is a default payer
    try {
      const customerConfig = await _parent.configs.getCustomerConfig(customerId);
      const defaultPayer = customerConfig && customerConfig.default_payer ? customerConfig.default_payer : null;
      const thirdPartyRequired =
        customerConfig && customerConfig.require_third_party_payer ? customerConfig.require_third_party_payer : false;
      if (thirdPartyRequired && !moveReq.third_party_payer && defaultPayer && defaultPayer !== customerId)
        moveReq.third_party_payer = defaultPayer;
    } catch (error) {
      console.error('Failed to fetch customer config', error);
      return {
        success: false,
        customer_id: null,
        errors: [errors.buildApiError(errors.codes.INTERNAL_SERVER_ERROR, 'Could not fetch customer config')],
      };
    }

    // Define pickup and delivery
    const pickup = getPropValue(moveReq, `${customerFirst ? `customer_move` : `loaner_move`}.pickup_location`);
    const delivery = getPropValue(moveReq, `${customerFirst ? `loaner_move` : `customer_move`}.pickup_location`);

    const pickupAddress = _parent.locations.buildAddress(
      pickup.address_one,
      pickup.city,
      pickup.state,
      pickup.zip,
      pickup.address_two,
      pickup.full_address
    );
    const deliveryAddress = _parent.locations.buildAddress(
      delivery.address_one,
      delivery.city,
      delivery.state,
      delivery.zip,
      delivery.address_two,
      delivery.full_address
    );

    let pickupName = pickup.name;
    if (!pickupName) {
      if (pickup.full_address) pickupName = _parent.locations.defaultLocationName(pickup.full_address);
      else if (pickup.address_one && pickup.address_two)
        pickupName = _parent.locations.defaultLocationName(`${pickup.address_one} ${pickup.address_two}`);
      else if (pickup.address_one) pickupName = _parent.locations.defaultLocationName(pickup.address_one);
    }

    let deliveryName = delivery.name;
    if (!deliveryName) {
      if (delivery.full_address) deliveryName = _parent.locations.defaultLocationName(delivery.full_address);
      else if (delivery.address_one && delivery.address_two)
        deliveryName = _parent.locations.defaultLocationName(`${delivery.address_one} ${delivery.address_two}`);
      else if (delivery.address_one) deliveryName = _parent.locations.defaultLocationName(delivery.address_one);
    }

    log && console.log('pickupAddress', pickupAddress, deliveryAddress, pickupName, deliveryName);

    // Query lane or insert shell lane
    const lanes = await _parent.lanes.getLaneOrInsertShell(
      customerId,
      pickupName,
      deliveryName,
      pickupAddress,
      deliveryAddress,
      options
    );
    log && console.log('Lanes:', lanes);

    // Look for errors in the lane creation and return them
    if (lanes.errors) {
      return { errors: lanes.errors };
    }

    // Check that at least one of the locations on the new lane have a live region
    const laneOne = lanes[0];
    const pickupRegionStatus = laneOne && laneOne.pickup && laneOne.pickup.region && laneOne.pickup.region.status;
    const deliveryRegionStatus = laneOne && laneOne.delivery && laneOne.delivery.region && laneOne.delivery.region.status;
    if (pickupRegionStatus !== 'live' && deliveryRegionStatus !== 'live') {
      return { errors: [errors.codes.OUTSIDE_REGION] };
    }

    const laneDuration = laneOne.duration_sec;

    // Convert move request to our DB structure
    let convertedMoveReq = _parent.moves.convertPairedMovesToDBSchema(
      moveReq,
      workflowsets,
      slas,
      isConcierge,
      customerFirst,
      laneDuration
    );

    // Add customer and lane id to converted request
    convertedMoveReq[0].customer_id = customerId;
    convertedMoveReq[0].lane_id = lanes[0].id;
    convertedMoveReq[1].customer_id = customerId;
    convertedMoveReq[1].lane_id = lanes[1].id;

    // Build based on the converted move request
    const buildMoveRes = await _parent.moves.buildConciergeLoanerMoves(convertedMoveReq, customerFirst);

    // Set response object
    let moveRes = {};

    // If returned validation from buildMoveRes passes, then convert the buildMoveRes output to the API response structure
    // If returned validation from buildMoveRes fails, then return the errors
    if (buildMoveRes.success === true)
      moveRes = _parent.moves.convertPairedMovesToAPISchema(buildMoveRes.output, isConcierge, customerFirst);
    else moveRes = { errors: buildMoveRes.errors };

    // TBD - Check the moveRes; process and return either error or success
    return moveRes;
  },

  flattenLocations: function (locations = []) {
    return (locations || []).map(p => {
      let alt = { ...p };
      delete alt.location;
      //alt.location = p.location.coordinates;
      //Use sep lat lon to match location table format
      if (p && p.location && p.location.coordinates && p.location.coordinates.length === 2) {
        alt.lat = p.location.coordinates[1];
        alt.lon = p.location.coordinates[0];
      } else {
        alt.lat = 0.0;
        alt.lon = 0.0;
      }
      return alt;
    });
  },

  /**
   * Function used by API to get move
   *
   * @param {Object} moveData - object returned by gql move query
   * @returns {Object} API response object
   */
  buildGetResponseObj: function (moveData) {
    // Get lane id from move data
    let laneId = getPropValue(moveData, 'lane.id');

    // Construct each response value from the queried move data
    let response = {
      ready_by_time_utc: moveData.ready_by,
      deliver_by_time_utc: moveData.deliver_by,
      earliest_available_time: moveData.earliest_available_time,
    };

    // Build move object
    let move = {
      id: moveData.id,
      rooftop: moveData.customer_id, //20220816 Rob - Added since allowed customers allows multiple
      third_party_payer: moveData.payer_id,
      reference_num: moveData.reference_num,
      status: moveData.status,
      cancel_status: moveData.cancel_status || 'not cancelled',
      cancel_reason: moveData.cancel_reason,
      pickup_started: moveData.pickup_started,
      pickup_arrived: moveData.pickup_arrived,
      pickup_successful: moveData.pickup_successful,
      delivery_started: moveData.delivery_started,
      delivery_arrived: moveData.delivery_arrived,
      delivery_successful: moveData.delivery_successful,
      dealer_contact: moveData.dealer_contact,
      special_instructions: moveData.move_details,
      settled: moveData.settled,
      move_uri: `https://api.hopdrive.com/v1/moves/${moveData.id}`,
      lane_uri: `https://api.hopdrive.com/v1/lanes/${laneId}`,
      portal_url: `https://dealer.hopdrive.io/moves/${moveData.id}`,
    };

    // Build vehicle object
    let vehicle = {
      vin: moveData.vehicle_vin,
      stock: moveData.vehicle_stock,
      year: moveData.vehicle_year,
      make: moveData.vehicle_make,
      model: moveData.vehicle_model,
      color: moveData.vehicle_color,
      manual: moveData.manual_flag,
    };

    // Build workflowset object, if present
    let workflowset = null;
    if (moveData.workflowset)
      workflowset = {
        id: moveData.workflowset.id,
        name: moveData.workflowset.name,
        label: moveData.workflowset.label || moveData.workflowset.name,
        description: moveData.workflowset.description,
      };

    // Assign vehicle and workflowset to move
    move.vehicle = vehicle;
    move.workflowset = workflowset;

    // Location response objects
    let pickupObj = getPropValue(moveData, 'lane.pickup');
    let deliveryObj = getPropValue(moveData, 'lane.delivery');
    let pickup_location = {
      location_id: pickupObj.id,
      name: pickupObj.name,
      lat: pickupObj.latitude,
      lon: pickupObj.longitude,
      google_place_id: pickupObj.place_id,
      full_address: pickupObj.address,
    };
    let delivery_location = {
      location_id: deliveryObj.id,
      name: deliveryObj.name,
      lat: deliveryObj.latitude,
      lon: deliveryObj.longitude,
      google_place_id: deliveryObj.place_id,
      full_address: deliveryObj.address,
    };

    // Combine objects to build response
    response.move = move;
    response.pickup_location = pickup_location;
    response.delivery_location = delivery_location;

    // Add vehicle photos to response
    response.pickup_photos = this.flattenLocations((moveData && moveData.pickup_photos) || []);
    response.delivery_photos = this.flattenLocations((moveData && moveData.delivery_photos) || []);

    // Remove private fields from workflow data and photos
    const privateStepIds = _parent.utilities.getPrivateStepIds(moveData.workflowset);
    response.move.pickup_workflow_data = _parent.utilities.removePrivateStepsFromWorkflowData(
      moveData.pickup_workflow_data || {},
      privateStepIds
    );
    response.move.delivery_workflow_data = _parent.utilities.removePrivateStepsFromWorkflowData(
      moveData.delivery_workflow_data || {},
      privateStepIds
    );
    response.pickup_photos = _parent.utilities.removePrivateStepsFromPhotos(response.pickup_photos, privateStepIds);
    response.delivery_photos = _parent.utilities.removePrivateStepsFromPhotos(response.delivery_photos, privateStepIds);

    // Add charges if present
    let charge;
    if (moveData.charge) {
      charge = {
        invoice_id: moveData.charge.invoice_id,
        status: moveData.charge.status,
        type: moveData.charge.type,
        due_amount: moveData.charge.due_amount,
        paid_amount: moveData.charge.paid_amount,
        balance_remaining: moveData.charge.due_amount - moveData.charge.paid_amount - moveData.charge.discount_amount,
        details: moveData.charge.details,
        discount_amount: moveData.charge.discount_amount,
        discount_reason: moveData.charge.discount_reason,
        disputed: moveData.charge.disputed,
        dispute_reason: moveData.charge.dispute_reason,
      };
    }

    response.charge = charge || {};

    // Return the built response
    return response;
  },

  /**
   * Function used by API to get move
   *
   * @param {number} moveId - id of the move to get
   * @param {number} customerId - id of the customer getting moves (limits which moves can be fetched)
   * @returns {Object} Returns an API response object (success or error)
   */
  moveGetter: async function (moveId, customerId) {
    if (!moveId || isNaN(moveId)) {
      return { errors: [errors.buildAuthenticationError(errors.codes.MOVE_INVALID, 'Please provide a Move ID')] };
    }
    if (!customerId || isNaN(customerId)) {
      return {
        errors: [errors.buildInvalidRequestError(errors.codes.MISSING_SCOPE, 'No Customer ID credential found')],
      };
    }
    let move = await _parent.moves.getById(moveId, customerId);
    if (move.success) {
      if (move.data && move.data.length > 0) {
        //return structured response obj
        let moveRes = this.buildGetResponseObj(move.data[0]);
        return moveRes;
      } else {
        //return error "no move found"
        return { errors: [errors.buildInvalidRequestError(errors.codes.NO_MOVE_FOUND)] };
      }
    } else {
      if (move.errors > 0) {
        //set errors with error handler function and return errors response
        return { errors: [errors.buildApiError(errors.codes.INTERNAL_SERVER_ERROR)] };
      }
    }
  },

  /**
   * Function to verify that user has correct claims to make requested move
   *
   * @param {Object} moveReq - request of move to create
   * @param {Object} claims - user claims object contains role, customer_id, and if dealer-admin contains allowed customers, allowed payers and allowed payees
   * @returns {Object} Returns response object with success, customer_id, and errors (if failure)
   */
  verifyClaims: async function (moveReq, claims) {
    const role = claims['x-hasura-default-role'];
    let allowed_customers = [];
    let allowed_payers = [];
    let allowed_payees = [];
    let customer_id = null;
    let defaultPayer = null;
    let payer_id = moveReq && moveReq.third_party_payer ? moveReq.third_party_payer : defaultPayer;
    let thirdPartyRequired = false;

    //Setup: get the three customer arrays from the claims object that will be used for authorization
    try {
      const customerClaims =
        claims && claims['x-hasura-allowed-customers']
          ? claims['x-hasura-allowed-customers'].replace('{', '[').replace('}', ']')
          : null;
      allowed_customers = JSON.parse(customerClaims);
      allowed_customers.push(Number(claims['x-hasura-customer-id']));
    } catch (error) {
      console.error('Failed to parse allowed customer array', error);
      return {
        success: false,
        customer_id: null,
        errors: [errors.buildApiError(errors.codes.ACCESS_DENIED, 'Invalid bearer token')],
      };
    }
    try {
      const payerClaims =
        claims && claims['x-hasura-allowed-payers']
          ? claims['x-hasura-allowed-payers'].replace('{', '[').replace('}', ']')
          : null;
      allowed_payers = JSON.parse(payerClaims);
    } catch (error) {
      console.error('Failed to parse allowed payer array', error);
      return {
        success: false,
        customer_id: null,
        errors: [errors.buildApiError(errors.codes.ACCESS_DENIED, 'Invalid bearer token')],
      };
    }
    try {
      const payeeClaims =
        claims && claims['x-hasura-payer-child-customers']
          ? claims['x-hasura-payer-child-customers'].replace('{', '[').replace('}', ']')
          : null;
      allowed_payees = JSON.parse(payeeClaims);
    } catch (error) {
      console.error('Failed to parse payer child customers array', error);
      return {
        success: false,
        customer_id: null,
        errors: [errors.buildApiError(errors.codes.ACCESS_DENIED, 'Invalid bearer token')],
      };
    }

    // Set customer_id of response
    const rooftop = moveReq && moveReq.rooftop ? moveReq.rooftop : null;
    if (role === 'dealer-admin') {
      if (!allowed_customers.includes(rooftop)) {
        return {
          success: false,
          customer_id: null,
          errors: [
            errors.buildApiError(
              errors.codes.REQUEST_INVALID,
              'You are not authorized to create moves for this rooftop'
            ),
          ],
        };
      }
      customer_id = rooftop;
      console.log(`Used customer_id ${customer_id} from request body`);
    } else if (role === 'dealer') {
      if (rooftop) {
        return {
          success: false,
          customer_id: null,
          errors: [
            errors.buildApiError(
              errors.codes.REQUEST_INVALID,
              'You are not authorized to create moves for this rooftop'
            ),
          ],
        };
      }
      // If they didn't incorrectly try to override, then use the claim value
      customer_id = claims['x-hasura-customer-id'];
      console.log(`Used customer_id ${customer_id} from JWT claims`);
    }

    if (isNaN(customer_id)) {
      return {
        success: false,
        customer_id: null,
        errors: [errors.buildApiError(errors.codes.MOVE_INVALID, `customer_id: ${customer_id} is not a number`)],
      };
    }
    customer_id = Number(customer_id);

    //Get customer config to check for default payer and if third party payer is required
    try {
      const customerConfig = await _parent.configs.getCustomerConfig(customer_id);
      thirdPartyRequired =
        customerConfig && customerConfig.require_third_party_payer ? customerConfig.require_third_party_payer : false;
      defaultPayer = customerConfig && customerConfig.default_payer ? customerConfig.default_payer : null;
    } catch (error) {
      console.error('Failed to fetch customer config', error);
      return {
        success: false,
        customer_id: null,
        errors: [errors.buildApiError(errors.codes.INTERNAL_SERVER_ERROR, 'Could not fetch customer config')],
      };
    }

    if (thirdPartyRequired && !payer_id && !defaultPayer) {
      return {
        success: false,
        customer_id: null,
        errors: [errors.buildApiError(errors.codes.REQUEST_INVALID, 'Third party payer is required for this customer')],
      };
    }

    // Validate payer_id
    if (payer_id && rooftop && payer_id === rooftop) {
      return {
        success: false,
        customer_id: customer_id,
        errors: [
          errors.buildApiError(
            errors.codes.REQUEST_INVALID,
            'Third party payer cannot be the same as rooftop- if you want the rooftop to pay for the move, do not use the third_party_payer request field'
          ),
        ],
      };
    }
    //If the requested payer_id equals the user's customer_id, they are the payer and we check if they are allowed to pay for the rooftop selected
    if (payer_id && payer_id === Number(claims['x-hasura-customer-id']) && !allowed_payees.includes(rooftop)) {
      return {
        success: false,
        customer_id: customer_id,
        errors: [
          errors.buildApiError(
            errors.codes.REQUEST_INVALID,
            'You are not authorized to pay for moves for this rooftop'
          ),
        ],
      };
    }
    //If the requested payer_id does not equal the user's customer_id, they are the payee and we check if they are allowed to assign payment to the requested third party payer
    if (payer_id && payer_id !== Number(claims['x-hasura-customer-id']) && !allowed_payers.includes(payer_id)) {
      return {
        success: false,
        customer_id: customer_id,
        errors: [
          errors.buildApiError(errors.codes.REQUEST_INVALID, 'You are not authorized to assign payment to this payer'),
        ],
      };
    }

    //success response if all above pass
    return {
      success: true,
      customer_id: customer_id,
      errors: null,
    };
  },

  checkAcertus: async function (moveReq) {
    const prodTypes = {
      24: 'acertus-hub',
      25: 'acertus-ford',
    };
    const testTypes = {
      37: 'acertus-hub',
      24: 'acertus-ford',
      23: 'acertus-element',
    };
    const types = process.env.REACT_APP_ENV === 'test' ? testTypes : prodTypes;
    const acertusIds = Object.keys(types).map(Number);
    const workflowsetId =
      getPropValue(moveReq, 'move.workflowset_id') ||
      getPropValue(moveReq, 'customer_move.workflowset_id') ||
      getPropValue(moveReq, 'loaner_move.workflowset_id');

    if (acertusIds.includes(workflowsetId)) {
      const customerId = moveReq.rooftop;
      const acertusCheck = await this.getWorkflowsetByType(types[workflowsetId], customerId);
      return acertusCheck;
    } else {
      return null;
    }
  },
};

module.exports = moves;